import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GlobalService } from '../../services/global.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridListComponent } from "../../components/ag-grid-list/ag-grid-list.component";
import { ButtonComponent } from "../../components/button/button.component";
import { HeaderComponent } from "../../components/header/header.component";
import { PluralPipe } from '../../pipes/plural.pipe';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { DetailedSelectInputComponent } from '../../components/detailed-select-input/detailed-select-input.component';
import { CommonModule } from '@angular/common';
import { DropdownService } from '../../services/dropdown.service';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { ValidationService } from '../../services/validation.service';
import { TableDropdownComponent } from '../../components/table-dropdown/table-dropdown.component';
import { UserService } from '../../services/user.service';
import { AssignmentService } from '../../services/assignment.service';
import { ReEvaluationService } from '../../services/re-evaluation.service';

@Component({
  selector: 'app-evaluator-mapping-list-page',
  imports: [HeaderComponent, ButtonComponent, CommonModule, ModalComponent, ReactiveFormsModule, FormsModule, PluralPipe, SelectInputComponent, AgGridListComponent, SearchInputComponent, DetailedSelectInputComponent],
  templateUrl: './evaluator-mapping-list-page.component.html',
  styleUrl: './evaluator-mapping-list-page.component.css'
})
export class EvaluatorMappingListPageComponent {
  @ViewChild('form_modal') form_modal: ModalComponent | undefined;
  @ViewChild('list') list!: AgGridListComponent;

  get page_name() { return this.is_re_evaluation ? 'Assign Re-Evaluators' : 'Assign Evaluators'; }
  form: FormGroup;
  params: any = {};
  selected_ids: any = {}
  items: any = [];
  isSubmitting = false;
  filtered_evaluator_list: any[] = [];

  filteredUniversities: any[] = [];
  filteredAcademicYears: any[] = [];
  filteredExams: any[] = [];
  filteredAssessments: any[] = [];
  lastUniversityId: any = null;
  columns: any = [];

  selected_level = 1;
  levels: number[] = [];
  current_evaluation_level = 1;
  unassigned_count = 0;
  level_stats: { level: number; total: number; assigned: number; complete: boolean }[] = [];
  is_re_evaluation = false;
  last_assigned_evaluator_id: any = null;

  /** Map of script_id → ReEvaluationRequest.id, populated only in re-evaluation mode
   *  so onEvaluatorChange can hand the right request_id to assign-evaluators. */
  re_eval_request_id_by_script: { [script_id: number]: number } = {};

  constructor(
    public gs: GlobalService,
    public route: Router,
    public fb: FormBuilder,
    public ar: ActivatedRoute,
    public dds: DropdownService,
    public vs: ValidationService,
    public us: UserService,
    public as: AssignmentService,
    public res: ReEvaluationService,
  ) {
    this.form = this.fb.group({
      evaluator_id: ['', [Validators.required]],
    });
  }

  async ngOnInit() {
    await this.dds?.get_dropdown_list(['university', 'academic-year', 'exam', 'manual_assessment', 'assessment', 'assessment_data']);
    this.filtered_evaluator_list = [];
    this.initializeColumns();
    this.rebuild_filter_lists();

    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params };
      // Access Level: lock the filters this user cannot change before the first fetch.
      this.gs.apply_access_scope_to_params(this.params);
      this.is_re_evaluation = this.params.evaluation_type === 'Re-Evaluation';
      this.rebuild_filter_lists();

      if (!this.params.assessment_id) {
        // Auto-select latest assessment only on fresh page load (no filters set yet)
        const no_filters = !this.params.university_id && !this.params.academic_year_id && !this.params.exam_id;
        if (no_filters) {
          const all_assessments: any[] = this.dds.dropdown_list?.assessment_list || [];
          if (all_assessments.length) {
            const latest = all_assessments.reduce((prev: any, curr: any) => (curr.id > prev.id ? curr : prev));
            this.params = {
              ...this.params,
              university_id: latest.university_id,
              academic_year_id: latest.academic_year_id,
              exam_id: latest.exam_id,
              assessment_id: latest.value ?? latest.id,
            };
            this.rebuild_filter_lists();
            this.route.navigate([], { relativeTo: this.ar, queryParams: { ...this.params }, replaceUrl: true });
            return;
          }
        }
        this.levels = [];
        this.items = [];
        return;
      }

      if (this.params.assessment_id) {
        const manual = this.dds.dropdown_list?.manual_assessment_list || [];
        const assessment_list = this.dds.dropdown_list?.assessment_list || [];
        const selected_assessment = manual.find((a: any) => a.id == this.params.assessment_id)
          || assessment_list.find((a: any) => a.id == this.params.assessment_id);

        // Re-evaluation: always level 1, no level chips
        if (this.is_re_evaluation) {
          this.selected_level = 1;
          this.levels = [];
        } else {
          const no_of_evaluation = Number(selected_assessment?.no_of_evaluation) || 1;
          this.current_evaluation_level = Number(selected_assessment?.current_evaluation_level) || 1;
          this.selected_level = 1;
          this.levels = Array.from({ length: no_of_evaluation }, (_, i) => i + 1);
        }

        const subject_id = selected_assessment?.subject_id || selected_assessment?.Subject?.id;
        if (subject_id) {
          try {
            const evaluator_filter: any = { is_active: true, page_size: 999999999, sort_by: 'name', sort_order: 'ASC', role: 'Evaluator', subject_id: +subject_id };
            if (this.params.university_id) evaluator_filter.university_id = +this.params.university_id;
            const res = await this.us.get_list(evaluator_filter);
            this.filtered_evaluator_list = (res?.data || []).map((item: any) => ({ ...item, title: item.name || 'none', value: item.id || 'none' }));
          } catch (err) {
            console.error('Failed to load evaluators', err);
            this.filtered_evaluator_list = [];
          }
        } else {
          this.filtered_evaluator_list = [];
        }
        this.initializeColumns();

        await this.get_list();
      } else {
        this.levels = [];
        this.items = [];
      }
    });
  }

  is_level_unlocked(level: number): boolean {
    if (level === 1) return true;
    for (let l = 1; l < level; l++) {
      const stat = this.level_stats.find(s => s.level === l);
      if (!stat || stat.total === 0 || stat.assigned < stat.total) return false;
    }
    return true;
  }

  async select_level(level: number) {
    if (!this.is_level_unlocked(level)) return;
    this.selected_level = level;
    await this.get_list();
  }

  rebuild_filter_lists() {
    const all: any[] = this.dds.dropdown_list?.assessment_data_list || [];
    const source = this.is_re_evaluation
      ? all.filter((a: any) => a.re_evaluation_required === true)
      : all;

    const _uni_seen = new Set();
    this.filteredUniversities = source
      .filter((a: any) => { const id = a.university?.id; if (!id || _uni_seen.has(id)) return false; _uni_seen.add(id); return true; })
      .map((a: any) => ({ id: a.university?.id, title: a.university?.name || 'none', value: a.university?.id || 'none' }));

    if (this.params.university_id) {
      const _ay_seen = new Set();
      this.filteredAcademicYears = source
        .filter((a: any) => a.university?.id == this.params.university_id)
        .filter((a: any) => { const id = a.academic_year?.id; if (!id || _ay_seen.has(id)) return false; _ay_seen.add(id); return true; })
        .map((a: any) => ({ ...a, title: a.academic_year?.name || 'none', value: a.academic_year?.id || 'none' }));
    } else {
      this.filteredAcademicYears = [];
    }

    if (this.params.university_id && this.params.academic_year_id) {
      const _exam_seen = new Set();
      this.filteredExams = source
        .filter((a: any) => a.university?.id == this.params.university_id && a.academic_year?.id == this.params.academic_year_id)
        .filter((a: any) => { const id = a.exam?.id; if (!id || _exam_seen.has(id)) return false; _exam_seen.add(id); return true; })
        .map((a: any) => ({ ...a, title: (a.exam?.name || 'none') + (a.exam?.code ? ' (' + a.exam.code + ')' : ''), value: a.exam?.id || 'none' }));
    } else {
      this.filteredExams = [];
    }

    if (this.params.university_id && this.params.academic_year_id && this.params.exam_id) {
      this.filteredAssessments = source
        .filter((a: any) => a.university?.id == this.params.university_id && a.academic_year?.id == this.params.academic_year_id && a.exam?.id == this.params.exam_id)
        .map((a: any) => ({ ...a, title: (a.subject?.name || 'none') + (a.subject?.code ? ' (' + a.subject.code + ')' : '') }));
    } else {
      this.filteredAssessments = [];
    }
  }

  // Locked once either the regular evaluation or the head evaluation is Completed for this
  // script. Re-Evaluation mode's rows don't carry evaluation_status/head_evaluation_status at
  // all (get_list() maps a different shape from the re-eval queue endpoint, see below) — its
  // already-computed `status` field ('Assigned'/'Unassigned'/'Completed', derived from the
  // ReEvaluationRequest's own status) is the equivalent signal there.
  is_evaluation_locked(row: any): boolean {
    if (this.is_re_evaluation) {
      return row?.status === 'Completed';
    }
    return row?.evaluation_status === 'Completed' || row?.head_evaluation_status === 'Completed';
  }

  initializeColumns() {
    this.columns = [
      { field: 'script_id', headerName: 'Script ID', filter: true, flex: 1 },
      { field: 'barcode', headerName: 'Barcode', filter: true, flex: 1 },
      {
        field: 'head_evaluator.name', headerName: 'Head Evaluator', filter: true, flex: 1,
        cellRenderer: (params: any) => {
          const name = params.data?.head_evaluator?.name;
          return name
            ? `<div class="flex items-center h-full">${name}</div>`
            : `<div class="flex items-center h-full"><span style="color:#b0b0b0;font-style:italic;">-</span></div>`;
        }
      },
      {
        field: 'status', headerName: 'Status', filter: true, flex: 1,
        cellRenderer: (params: any) => {
          const value = params.value || 'Unassigned';
          const isAssigned = value === 'Assigned';
          return `<div class="flex items-center h-full"><span class="inline-block px-5 py-1.5 rounded-full text-sm font-semibold ${isAssigned ? 'bg-green-100 text-[#1a7a4a]' : 'bg-[#F7F7F7] text-gray-500'}">${value}</span></div>`;
        }
      },
      {
        field: 'level_evaluator_id',
        headerName: this.is_re_evaluation ? 'Re-Evaluator Name' : 'Evaluator Name',
        filter: true,
        flex: 2,
        cellRenderer: TableDropdownComponent,
        cellRendererParams: {
          // Once evaluation or head evaluation is Completed for this script, lock the evaluator
          // field entirely (on top of the existing permission check) so it can't be reassigned.
          disabled: (row: any) => {
            if (!this.gs.user_role_data?.assignments_assign_evaluation_assign) return true;
            return this.is_evaluation_locked(row);
          },
          // Same condition hides the clear ("x") too, though with the field disabled it's
          // effectively redundant now — kept for the (permitted, not-yet-completed) case.
          clearable: (row: any) => !this.is_evaluation_locked(row),
          options: this.filtered_evaluator_list,
          placeholder: 'Select Evaluator',
          show_email: true,
          onChange: this.onEvaluatorChange.bind(this),
        }
      },
    ];
  }

  /* Dropdown Dependency Logic */
  dropdownRegistry: { [depended: number]: any } = {};

  registerDropdown(event: { depended: number; ref: any }) {
    this.dropdownRegistry[event.depended] = event.ref;
  }

  async onDropdownChange(event: { value: any, depended: number }) {
    const queryParams: any = { ...this.params };

    if (event.depended == 1) {
      if (event.value) { queryParams.university_id = event.value.value; } else { delete queryParams.university_id; }
      delete queryParams.academic_year_id;
      delete queryParams.exam_id;
      delete queryParams.assessment_id;
      this.items = [];
    }
    if (event.depended == 2) {
      if (event.value) { queryParams.academic_year_id = event.value.value; } else { delete queryParams.academic_year_id; }
      delete queryParams.exam_id;
      delete queryParams.assessment_id;
      this.items = [];
    }
    if (event.depended == 3) {
      if (event.value) { queryParams.exam_id = event.value.value; } else { delete queryParams.exam_id; }
      delete queryParams.assessment_id;
      this.items = [];
    }
    if (event.depended == 4) {
      if (event.value) { queryParams.assessment_id = event.value.value; } else { delete queryParams.assessment_id; this.items = []; }
    }

    this.params = queryParams;
    this.rebuild_filter_lists();

    this.route.navigate([], { relativeTo: this.ar, queryParams });
  }

  async onEvaluatorChange(new_value: any, row_data: any) {
    try {
      if (!row_data.id) return;

      if (this.is_re_evaluation) {
        // In re-eval mode each row carries a ReEvaluationRequest.id; route the pick
        // through assign-evaluators (Phase 2) so request lifecycle + audit fire.
        const request_id = this.re_eval_request_id_by_script[row_data.id] ?? row_data.request_id;
        if (!request_id) {
          this.gs.toastr_shows_function('No re-evaluation request found for this script', 'Error', 'error');
          return;
        }
        if (!new_value) {
          this.gs.toastr_shows_function('Unassign is not supported for re-evaluation; pick a different evaluator instead', 'Info', 'info');
          return;
        }
        await this.res.assign_evaluators([{ request_id, evaluator_id: new_value }]);
        await this.get_list();
        this.gs.toastr_shows_function('Re-Evaluator Assigned Successfully.', 'Success', 'success');
        return;
      }

      await this.as.assign_evaluator({
        assessment_id: +this.params.assessment_id,
        evaluator_id: new_value || null,
        script_ids: [row_data.id],
        level: this.selected_level,
        evaluation_type: 'Evaluation',
      });
      if (new_value) this.last_assigned_evaluator_id = new_value;
      await this.get_list();
      const msg = new_value ? 'Evaluator Assigned Successfully.' : 'Evaluator Unassigned Successfully.';
      this.gs.toastr_shows_function(msg, 'Success', 'success');
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Error updating evaluator', 'Error', 'error');
    }
  }

  async get_list(filter?: any) {
    try {
      if (!this.params.assessment_id) return;

      if (this.is_re_evaluation) {
        // Pull pending + in-progress re-eval requests for this assessment from
        // the new BE queue endpoint, then map into the row shape this page uses.
        const re_eval_res: any = await this.res.get_list({
          assessment_id: this.params.assessment_id,
          page: filter?.page || 1,
          page_size: filter?.page_size || 100,
          sort_by: filter?.sort_by || 'requested_at',
          sort_order: filter?.sort_order || 'DESC',
        });
        this.re_eval_request_id_by_script = {};
        const rows = (re_eval_res?.data || []).map((req: any) => {
          const script = req.script || {};
          const status_lc = String(req.status || '').toLowerCase();
          // Multi-attempt: a script can have multiple ReEvaluationRequest rows
          // (old completed + new pending). Only map non-terminal requests so the
          // assign flow targets the active attempt — never a closed historical one.
          if (script.id && (status_lc === 'pending' || status_lc === 'in_progress')) {
            this.re_eval_request_id_by_script[script.id] = req.id;
          }
          return {
            id: script.id,
            request_id: req.id,
            script_id: script.script_id || script.barcode || `#${script.id}`,
            barcode: script.barcode || '-',
            level_evaluator_id: req.evaluator_id ?? null,
            // Show the ACTUALLY-ASSIGNED HE on the script first (admin's pick),
            // fall back to the evaluator's hierarchical HE only when no HE has
            // been mapped yet. Old order silently showed the wrong name when
            // admin had overridden the hierarchy.
            head_evaluator: script.re_evaluation_assigned_head_evaluator?.name
              ? { id: script.re_evaluation_assigned_head_evaluator_id, name: script.re_evaluation_assigned_head_evaluator.name }
              : req.evaluator?.HeadEvaluator?.name
                ? { id: req.evaluator.HeadEvaluator.id, name: req.evaluator.HeadEvaluator.name }
                : null,
            status: status_lc === 'pending'
              ? (req.evaluator_id ? 'Assigned' : 'Unassigned')
              : status_lc === 'in_progress'
                ? 'Assigned'
                : status_lc === 'completed'
                  ? 'Completed'
                  : 'Unassigned',
          };
        });
        this.items = {
          data: rows,
          count: re_eval_res?.pagination?.total_records ?? rows.length,
          pagination: re_eval_res?.pagination,
        };
        this.level_stats = [];
        this.unassigned_count = rows.filter((r: any) => !r.level_evaluator_id).length;
        return;
      }

      const res = await this.as.get_list({
        assessment_id: this.params.assessment_id,
        level: this.selected_level,
        evaluation_type: 'Evaluation',
        page: filter?.page || 1,
        page_size: filter?.page_size || 10,
        sort_by: filter?.sort_by,
        sort_order: filter?.sort_order,
      });
      this.items = res;
      this.level_stats = res?.level_stats || [];
      const stat = this.level_stats.find(s => s.level === this.selected_level);
      this.unassigned_count = stat ? stat.total - stat.assigned : 0;
    } catch (error: any) {
      console.error('Error fetching assignment list:', error);
    }
  }

  open_form_modal() {
    this.form.reset();
    if (this.last_assigned_evaluator_id) {
      this.form.patchValue({ evaluator_id: this.last_assigned_evaluator_id });
    }
    this.form_modal?.open();
  }

  close_form_modal(): void {
    this.form_modal?.close();
  }

  async submit() {
    try {
      if (this.isSubmitting) return;
      if (this.form.invalid) {
        this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
        this.form.markAllAsTouched();
        return;
      }
      this.isSubmitting = true;
      const evaluator_id = this.form.value.evaluator_id;
      if (this.is_re_evaluation) {
        const assignments = (this.list.selected_ids || [])
          .map((script_id: number) => this.re_eval_request_id_by_script[script_id])
          .filter((rid: number | undefined): rid is number => !!rid)
          .map((request_id: number) => ({ request_id, evaluator_id }));
        if (!assignments.length) {
          this.gs.toastr_shows_function('Pick at least one row with a re-evaluation request', 'Info', 'info');
          return;
        }
        await this.res.assign_evaluators(assignments);
      } else {
        await this.as.assign_evaluator({
          assessment_id: +this.params.assessment_id,
          evaluator_id,
          script_ids: this.list.selected_ids,
          level: this.selected_level,
          evaluation_type: 'Evaluation',
        });
      }
      this.last_assigned_evaluator_id = evaluator_id;
      this.gs.toastr_shows_function('Evaluator Assigned Successfully.', 'Success', 'success');
      await this.get_list();
      this.close_form_modal();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Error assigning evaluator', 'Error', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }
}
